import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import logoImg from '../../assets/logo.png';
import { createUserWithEmailAndPassword, deleteUser, signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { setUserRole } from '../../services/authService';
import { db } from '../../config/firebase';
import { doc, setDoc } from 'firebase/firestore';
import styles from './Login.module.css';
import { inviteService } from '../../services/inviteService';
import TermsOfService from '../../components/common/TermsOfService';
import PrivacyPolicy from '../../components/common/PrivacyPolicy';
import ConsentManager from '../../components/common/ConsentManager';
import Terms from '../../components/common/Terms';
import RefundPolicy from '../../components/common/RefundPolicy';
import AccessibilityStatement from '../../components/common/AccessibilityStatement';
import Logger from '../../services/logger';
import ErrorTracker from '../../services/errorTracker';
import { getApiUrl } from '../../utils/backendUrl';
import {
  REGISTRATION_AUTHORITY_FAIL_FALLBACK,
  REGISTRATION_MC_DOT_ALREADY_REGISTERED_CODE,
  REGISTRATION_ALREADY_HAS_OWNER_CODE,
  REGISTRATION_INVITE_ONLY_CODE,
  REGISTRATION_POLICY
} from '../../constants/registration';
import { AUTH_PAGE_THEME_VARS } from '../../constants/authPageTheme';
import TranzitTurnstileWidget, { TranzitTurnstileWidgetHandle } from '../../components/common/TranzitTurnstileWidget';
import {
  getRegistrationAbuseUserMessage,
  getRegistrationTurnstileSiteKey,
  REGISTRATION_TURNSTILE_TOKEN_FIELD,
} from '../../constants/registrationAbuse';
import {
  buildOwnerRegistrationCompanyUserPayload,
  buildTeamInviteCompanyUserPayload,
} from '../../utils/companyUserClientRegistration';

type UserType = 'shipper' | 'carrier' | 'broker';
type BrokerSubType = 'broker_only' | 'broker_carrier';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    userType: 'shipper' as UserType,
    brokerSubType: 'broker_only' as BrokerSubType,
    companyName: '',
    companyRep: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    mcNumber: '',
    dotNumber: '',
    isOwner: true,
    registrationPolicy: REGISTRATION_POLICY.OPEN as 'invite_only' | 'open'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [authorityPassSuccess, setAuthorityPassSuccess] = useState(false);
  const [authorityJoinedAsTeamMember, setAuthorityJoinedAsTeamMember] = useState(false);
  const [authorityPendingApproval, setAuthorityPendingApproval] = useState(false);
  const [authorityEmailSent, setAuthorityEmailSent] = useState<boolean | null>(null);
  const [authorityFailReason, setAuthorityFailReason] = useState<string | null>(null);
  const [authorityMcDotAlreadyRegistered, setAuthorityMcDotAlreadyRegistered] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  // Phase 3: Claim ownership form (when MC/DOT already registered)
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimPassword, setClaimPassword] = useState('');
  const [claimConfirmPassword, setClaimConfirmPassword] = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimSuccess, setClaimSuccess] = useState(false);
  const registrationTurnstileSiteKey = useMemo(() => getRegistrationTurnstileSiteKey(), []);
  const [registrationTurnstileToken, setRegistrationTurnstileToken] = useState<string | null>(null);
  const registrationTurnstileRef = useRef<TranzitTurnstileWidgetHandle>(null);
  const inviteTokenFromUrl = new URLSearchParams(location.search).get('invite');
  const needsRegistrationTurnstile =
    !inviteTokenFromUrl &&
    (formData.userType === 'carrier' || formData.userType === 'broker' || showClaimForm);

  useEffect(() => {
    // Check for invite token in URL
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('invite');
    if (inviteToken) {
      setInviteLoading(true);
      // Fetch real invite data from backend
      const fetchInviteData = async () => {
        try {
          const inviteData = await inviteService.getInviteData(inviteToken);
          if (inviteData) {
            const inviteUserType = (inviteData.userType === 'broker' || inviteData.userType === 'shipper' || inviteData.userType === 'carrier')
              ? inviteData.userType
              : 'carrier';
            setFormData(prev => ({
              ...prev,
              userType: inviteUserType as UserType,
              brokerSubType: 'broker_only' as BrokerSubType,
              companyName: inviteData.companyName ?? prev.companyName,
              companyRep: inviteData.companyRep ?? prev.companyRep,
              phoneNumber: inviteData.phone ?? inviteData.phoneNumber ?? prev.phoneNumber,
              email: inviteData.email ?? prev.email,
              mcNumber: inviteData.mcNumber ?? prev.mcNumber,
              dotNumber: inviteData.dotNumber ?? prev.dotNumber
            }));
          } else {
            setError('Invalid or expired invitation link.');
          }
        } catch (error) {
          ErrorTracker.trackError(error as Error, 'Register fetch invite data', { data: { inviteToken: inviteToken } });
          setError('Failed to load invitation data. Please try again.');
        } finally {
          setInviteLoading(false);
        }
      };
      
      fetchInviteData();
    }
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate required fields including userType
    if (!formData.companyName.trim() || !formData.phoneNumber.trim() || !formData.email.trim() || !formData.password || !formData.confirmPassword || !formData.userType) {
      setError('Please fill out all required fields.');
      setLoading(false);
      return;
    }
    
    // Validate userType is a valid value
    const validUserTypes = ['shipper', 'carrier', 'broker'];
    if (!validUserTypes.includes(formData.userType)) {
      setError('Invalid user type selected. Please select a valid user type.');
      setLoading(false);
      return;
    }
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    // Validate terms acceptance
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy to continue');
      setLoading(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('invite');
    const isCarrierOrBroker = formData.userType === 'carrier' || formData.userType === 'broker';
    const useAuthorityFlow = isCarrierOrBroker && !inviteToken;

    if (useAuthorityFlow) {
      const hasMc = formData.mcNumber != null && String(formData.mcNumber).replace(/\D/g, '').length > 0;
      const hasDot = formData.dotNumber != null && String(formData.dotNumber).replace(/\D/g, '').length > 0;
      if (!hasMc && !hasDot) {
        setError('MC number or DOT number is required for carrier and broker registration.');
        setLoading(false);
        return;
      }
      if (registrationTurnstileSiteKey && !registrationTurnstileToken) {
        setError('Complete the security check before registering.');
        setLoading(false);
        return;
      }
    }

    try {
      if (useAuthorityFlow) {
        const apiUrl = getApiUrl();
        const res = await fetch(`${apiUrl}/auth/register-with-authority`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email.trim(),
            password: formData.password,
            companyName: formData.companyName.trim(),
            companyRep: formData.companyRep.trim(),
            phoneNumber: formData.phoneNumber.trim(),
            userType: formData.userType,
            mcNumber: formData.mcNumber?.trim() || null,
            dotNumber: formData.dotNumber?.trim() || null,
            isOwner: formData.isOwner,
            registrationPolicy: formData.isOwner ? formData.registrationPolicy : REGISTRATION_POLICY.OPEN,
            ...(registrationTurnstileToken
              ? { [REGISTRATION_TURNSTILE_TOKEN_FIELD]: registrationTurnstileToken }
              : {}),
          })
        });
        const responseText = await res.text();
        let data: Record<string, unknown> = {};
        try {
          data = JSON.parse(responseText) as Record<string, unknown>;
        } catch {
          console.error('[Registration] Response was not JSON. Status:', res.status, 'Body:', responseText?.slice(0, 500));
        }
        if (data.success && data.pass) {
          const joinedAsTeamMember = (data as { joinedAsTeamMember?: boolean }).joinedAsTeamMember === true;
          if (joinedAsTeamMember) {
            setAuthorityJoinedAsTeamMember(true);
            setAuthorityEmailSent(false);
          } else {
            setAuthorityEmailSent(data.emailSent === true);
          }
          setAuthorityPassSuccess(true);
          setLoading(false);
          return;
        }
        if (data.success && (data as { authorityCheckDisabled?: boolean }).authorityCheckDisabled) {
          setAuthorityPendingApproval(true);
          setLoading(false);
          return;
        }
        if (res.status >= 500) {
          console.error('[Registration] Server error:', res.status, 'Response:', data);
        }
        const code = (data as { code?: string }).code;
        const isInviteOnlyOrAlreadyHasOwner =
          res.status === 409 && (code === REGISTRATION_INVITE_ONLY_CODE || code === REGISTRATION_ALREADY_HAS_OWNER_CODE);
        if (isInviteOnlyOrAlreadyHasOwner) {
          setAuthorityMcDotAlreadyRegistered(false);
          setAuthorityFailReason((data.reason as string) || 'Registration not allowed for this company.');
        } else {
        const isMcDotAlreadyRegistered =
          res.status === 409 || (res.status === 400 && code === REGISTRATION_MC_DOT_ALREADY_REGISTERED_CODE);
        if (isMcDotAlreadyRegistered) {
          setAuthorityMcDotAlreadyRegistered(true);
          setAuthorityFailReason((data.reason as string) || 'This MC/DOT is already registered.');
        } else {
          setRegistrationTurnstileToken(null);
          registrationTurnstileRef.current?.reset();
          setAuthorityMcDotAlreadyRegistered(false);
          let reason =
            getRegistrationAbuseUserMessage(code) ||
            (typeof data.message === 'string' ? data.message : null) ||
            (data.reason as string) ||
            (res.status >= 500
              ? 'Registration failed. Please try again or contact support.'
              : REGISTRATION_AUTHORITY_FAIL_FALLBACK);
          const debug = data.debug as { message?: string; code?: string } | undefined;
          if (res.status >= 500 && debug) {
            reason = `${reason} (Server: ${debug.message ?? 'no details'}${debug.code ? ` [${debug.code}]` : ''})`;
          } else if (res.status >= 500) {
            const snippet = responseText?.slice(0, 200)?.replace(/\s+/g, ' ').trim() || '';
            reason = snippet
              ? `${reason} (Server response: ${snippet}${responseText.length > 200 ? '…' : ''})`
              : `${reason} (Check browser console: [Registration] Server error for full response.)`;
          }
          setAuthorityFailReason(reason);
        }
        }
        setLoading(false);
        return;
      }

      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Prepare user data - userType is REQUIRED and validated above
      // Create companyId for new registrations (same pattern as brokers)
      const companyId = `company_${userCredential.user.uid}`;
      const now = new Date();
      
      const userData: any = {
        // Required identification fields
        id: userCredential.user.uid,
        companyId: companyId,
        
        // User Information
        companyName: formData.companyName,
        companyRep: formData.companyRep,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        userType: formData.userType, // REQUIRED - validated above
        
        // Status and approval fields - REQUIRED for admin approval workflow
        status: 'offline', // User status (online/offline/away/busy)
        availability: 'offline', // User availability
        approvalStatus: 'pending', // REQUIRED for admin approval query
        isAdmin: false,
        isSuperAdmin: false,
        
        // Role and permissions - Phase 5: owner vs team member; invite path overwrites below
        role: formData.isOwner ? 'company_owner' : 'company_user',
        companyUserRole: formData.isOwner ? 'owner' : 'user',
        permissions: ['read'], // Default permissions for new users
        accessLevel: 'limited', // Default access level for new users
        
        // Timestamps - REQUIRED fields
        createdAt: now,
        updatedAt: now,
        lastChanged: now,
        
        // Add broker-specific data
        ...(formData.userType === 'broker' && {
          brokerSubType: formData.brokerSubType,
          isBroker: true,
          hasCarrierOperations: formData.brokerSubType === 'broker_carrier'
        })
      };

      // If this is an invited team member, use invite's company and role (Phase 4)
      if (inviteToken) {
        const inviteData = await inviteService.getInviteData(inviteToken);
        if (inviteData) {
          userData.role = 'company_user';
          userData.companyUserRole = 'user';
          userData.companyId = inviteData.companyId ?? inviteData.inviterId; // Prefer companyId (team invite); fallback inviterId (legacy driver invite)
          userData.parentCompanyId = inviteData.inviterId;
          userData.userType = (inviteData.userType === 'broker' || inviteData.userType === 'shipper' || inviteData.userType === 'carrier')
            ? inviteData.userType
            : formData.userType;
          if (inviteData.mcNumber != null) userData.mcNumber = inviteData.mcNumber;
          if (inviteData.dotNumber != null) userData.dotNumber = inviteData.dotNumber;
          await inviteService.markInviteAsUsed(inviteToken);
        }
      }
      // Note: Regular user registration already has role: 'company_owner' and companyUserRole: 'owner' set above
      
      // SECURITY: Final check - ensure userType is set (already validated above, but ensure it wasn't lost)
      if (!userData.userType) {
        throw new Error('User type is required. Registration cannot proceed without a valid user type.');
      }

      // Save user profile info to Firestore
      // Invited users: write only companyUsers (join existing company); then redirect to onboarding (Phase 4)
      if (inviteToken && userData.role === 'company_user' && userData.companyId) {
        try {
          await setDoc(
            doc(db, 'companyUsers', userCredential.user.uid),
            buildTeamInviteCompanyUserPayload(userData)
          );
        } catch (firestoreError) {
          ErrorTracker.trackError(firestoreError as Error, 'Register save invited user to Firestore', { data: { userId: userCredential.user.uid } });
          try {
            await deleteUser(userCredential.user);
          } catch (cleanupError) {
            ErrorTracker.trackError(cleanupError as Error, 'Register cleanup Firebase Auth user', { data: { userId: userCredential.user.uid } });
          }
          throw new Error(`Failed to save your account: ${firestoreError instanceof Error ? firestoreError.message : 'Unknown error'}`);
        }
        const ut = (userData.userType ?? 'carrier') as string;
        setLoading(false);
        if (ut === 'broker') {
          navigate('/broker/onboarding', { state: { fromInvite: true }, replace: true });
        } else if (ut === 'shipper') {
          navigate('/shipper/onboarding', { state: { fromInvite: true }, replace: true });
        } else {
          navigate('/carrier/onboarding', { state: { fromInvite: true }, replace: true });
        }
        return;
      }

      if (formData.userType === 'broker') {
        // For brokers (non-invite), use the new collection structure
        const companyId = `company_${userCredential.user.uid}`;
        
        // Create company record
        const companyData = {
          id: companyId,
          name: formData.companyName,
          type: formData.brokerSubType,
          status: 'pending',
          primaryOwnerId: formData.isOwner ? userCredential.user.uid : null,
          registrationPolicy: formData.isOwner ? formData.registrationPolicy : REGISTRATION_POLICY.OPEN,
          businessType: 'logistics',
          capabilities: ['freight_management', 'brokerage'],
          createdAt: new Date(),
          updatedAt: new Date(),
          verifiedAt: null,
          tags: ['broker_portal', 'new_registration'],
          notes: 'Registered through main registration form'
        };

        // Create company user record
        const companyUserData = {
          id: userCredential.user.uid,
          companyId: companyId,
          email: formData.email,
          companyName: formData.companyName,
          companyRep: formData.companyRep,
          phoneNumber: formData.phoneNumber,
          userType: formData.userType,
          companyUserRole: formData.isOwner ? 'owner' : 'user',
          role: formData.isOwner ? 'company_owner' : 'company_user',
          status: 'pending',
          approvalStatus: 'pending',
          isAdmin: false,
          isSuperAdmin: false,
          permissions: formData.brokerSubType === 'broker_carrier' 
            ? ['admin', 'load_management', 'carrier_management', 'user_management', 'carrier_operations']
            : ['admin', 'load_management', 'carrier_management', 'user_management'],
          accessLevel: 'full',
          createdAt: new Date(),
          updatedAt: new Date(),
          // Add broker-specific data
          brokerSubType: formData.brokerSubType,
          isBroker: true,
          hasCarrierOperations: formData.brokerSubType === 'broker_carrier',
          migratedFrom: {
            collection: 'main_registration',
            migratedAt: new Date(),
            source: 'main_registration_form'
          }
        };

              // Save to new collections
      try {
        await setDoc(doc(db, 'companies', companyId), companyData);
        await setDoc(
          doc(db, 'companyUsers', userCredential.user.uid),
          buildOwnerRegistrationCompanyUserPayload(companyUserData)
        );

      } catch (firestoreError) {
        ErrorTracker.trackError(firestoreError as Error, 'Register save company user to Firestore', { data: { userId: userCredential.user.uid, userType: formData.userType } });
        // Clean up the Firebase Auth user since Firestore save failed
        try {
          await deleteUser(userCredential.user);

        } catch (cleanupError) {
          ErrorTracker.trackError(cleanupError as Error, 'Register cleanup Firebase Auth user', { data: { userId: userCredential.user.uid } });
        }
        throw new Error(`Failed to save broker data: ${firestoreError instanceof Error ? firestoreError.message : 'Unknown error'}`);
      }
    } else {
      // Phase 0: Company-first for shipper — create companies doc then companyUsers (same pattern as broker)
      const companyId = userData.companyId;
      const companyData = {
        id: companyId,
        name: formData.companyName,
        type: formData.userType as 'shipper' | 'carrier',
        status: 'pending',
        primaryOwnerId: formData.isOwner ? userCredential.user.uid : null,
        registrationPolicy: formData.isOwner ? formData.registrationPolicy : REGISTRATION_POLICY.OPEN,
        businessType: 'logistics',
        capabilities: formData.userType === 'shipper' ? ['freight_management'] : ['freight_management', 'carrier_operations'],
        createdAt: new Date(),
        updatedAt: new Date(),
        verifiedAt: null,
        tags: ['new_registration'],
        notes: 'Registered through main registration form',
        ...(formData.mcNumber != null && String(formData.mcNumber).trim() ? { mcNumber: formData.mcNumber.trim() } : {}),
        ...(formData.dotNumber != null && String(formData.dotNumber).trim() ? { dotNumber: formData.dotNumber.trim() } : {})
      };
      try {
        await setDoc(doc(db, 'companies', companyId), companyData);
        await setDoc(
          doc(db, 'companyUsers', userCredential.user.uid),
          buildOwnerRegistrationCompanyUserPayload(userData)
        );
      } catch (firestoreError) {
        ErrorTracker.trackError(firestoreError as Error, 'Register save company/user to Firestore', { data: { userId: userCredential.user.uid, userType: formData.userType } });
        try {
          await deleteUser(userCredential.user);
        } catch (cleanupError) {
          ErrorTracker.trackError(cleanupError as Error, 'Register cleanup Firebase Auth user', { data: { userId: userCredential.user.uid } });
        }
        throw new Error(`Failed to save user data: ${firestoreError instanceof Error ? firestoreError.message : 'Unknown error'}`);
      }
    }

    try {
      await signOut(auth);
    } catch (signOutError) {
      Logger.warn('[Register] signOut after pending registration failed', {
        error: signOutError instanceof Error ? signOutError.message : String(signOutError),
      });
    }

    // Show success message instead of navigating to login
    setRegistrationSuccess(true);
  } catch (err) {
    ErrorTracker.trackError(err as Error, 'Register registration', { data: { userType: formData.userType, email: formData.email } });
    
    // Provide more specific error messages
    let errorMessage = 'Failed to create account. Please try again.';
    if (err instanceof Error) {
      if (err.message.includes('auth/email-already-in-use')) {
        errorMessage = 'An account with this email already exists. Please use a different email or try logging in.';
      } else if (err.message.includes('Failed to save')) {
        errorMessage = `Registration failed: ${err.message}. Please try again or contact support.`;
      } else if (err.message.includes('auth/weak-password')) {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (err.message.includes('auth/invalid-email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.includes('Network request failed') ||
        err.message.includes('Load failed')
      ) {
        errorMessage =
          'Could not reach the server. Make sure the backend is running (e.g. npm start in backend folder). ' +
          'If you use HTTPS locally, the app uses a proxy—restart the frontend after changing .env.';
      } else if (err.message.includes('REACT_APP_BACKEND_URL')) {
        errorMessage = 'App config: backend URL is not set. Check .env.development or .env.development.local.';
      }
    }
    
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
  };

  if (authorityPassSuccess) {
    const emailFailed = authorityEmailSent === false;
    if (authorityJoinedAsTeamMember) {
      return (
        <>
          <Helmet>
            <title>Added to company - Tranzit.io</title>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
            <div className={styles.loginBox}>
              <h1>You&apos;re all set</h1>
              <div className={styles.centerBlock}>
                <p>You have been added to the company. You can now log in with your email and password.</p>
              </div>
              <button className={styles.loginButton} onClick={() => navigate('/login')}>Go to Login</button>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <Helmet>
          <title>{emailFailed ? 'Registration complete' : 'Check Your Email'} - Tranzit.io</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
          <div className={styles.loginBox}>
            <h1>{emailFailed ? 'Registration complete' : 'Check your email'}</h1>
            <div className={styles.centerBlock}>
              <p>Your registration passed authority verification.</p>
              {emailFailed ? (
                <p className={styles.textDanger}>We couldn&apos;t send the confirmation email to <strong>{formData.email}</strong>. Your account was created—please contact Tranzit support with this email address to get your sign-in link.</p>
              ) : (
                <>
                  <p>We sent a link to <strong>{formData.email}</strong>. Click that link to finish setting up your account.</p>
                  <p>If you don&apos;t see it, check your spam folder. The link expires in 48 hours.</p>
                </>
              )}
            </div>
            <button
              className={styles.loginButton}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  if (authorityPendingApproval) {
    return (
      <>
        <Helmet>
          <title>Registration received - Tranzit.io</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
          <div className={styles.loginBox}>
            <h1>Registration received</h1>
            <div className={`${styles.centerBlock} ${styles.textMuted}`}>
              <p>Your registration has been submitted. You will be notified when your account is approved.</p>
              <p>You can then log in with <strong>{formData.email}</strong>.</p>
            </div>
            <button
              className={styles.loginButton}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  if (authorityFailReason) {
    const isServerError =
      /server|not fully configured|contact support|try again/i.test(authorityFailReason);
    const isEmailExists =
      /already exists|email already|account with this email/i.test(authorityFailReason);
    const isMcDotAlreadyRegistered = authorityMcDotAlreadyRegistered;
    const title = isMcDotAlreadyRegistered
      ? 'This MC/DOT is already registered'
      : isServerError
        ? 'Registration failed'
        : isEmailExists
          ? 'Email already in use'
          : 'MC/DOT verification did not pass';

    // Phase 3: Request ownership form (authorized company rep)
    if (isMcDotAlreadyRegistered && showClaimForm) {
      const isBrokerOrCarrier = formData.userType === 'broker' || formData.userType === 'carrier';
      return (
        <>
          <Helmet>
            <title>Request ownership - Tranzit.io</title>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
            <div className={styles.loginBox}>
              <h1>Request ownership</h1>
              <div className={`${styles.centerBlock} ${styles.textMuted}`}>
                <p style={{ marginBottom: 16 }}>
                  You are the authorized company representative. Create your account to become the company owner. The current account holder will become an admin.
                </p>
              </div>
              {claimSuccess ? (
                <div className={styles.centerBlock}>
                  <p className={styles.textSuccess} style={{ marginBottom: 16 }}>Ownership claim approved. Sign in to continue.</p>
                  <button type="button" className={styles.loginButton} onClick={() => navigate('/login')}>Go to Login</button>
                </div>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setClaimError('');
                  if (claimPassword !== claimConfirmPassword) {
                    setClaimError('Passwords do not match.');
                    return;
                  }
                  if (claimPassword.length < 6) {
                    setClaimError('Password must be at least 6 characters.');
                    return;
                  }
                  if (!isBrokerOrCarrier) {
                    setClaimError('Only broker or carrier can request ownership from this flow.');
                    return;
                  }
                  if (claimSubmitting) return;
                  if (registrationTurnstileSiteKey && !registrationTurnstileToken) {
                    setClaimError('Complete the security check before requesting ownership.');
                    return;
                  }
                  setClaimSubmitting(true);
                  try {
                    const result = await inviteService.requestOwnership({
                      email: formData.email.trim(),
                      password: claimPassword,
                      companyName: formData.companyName.trim(),
                      companyRep: formData.companyRep.trim(),
                      phoneNumber: formData.phoneNumber?.trim() || undefined,
                      mcNumber: formData.mcNumber?.trim() || undefined,
                      dotNumber: formData.dotNumber?.trim() || undefined,
                      userType: formData.userType as 'broker' | 'carrier',
                      turnstileToken: registrationTurnstileToken || undefined,
                    });
                    if (result.success) {
                      setClaimSuccess(true);
                    } else {
                      setRegistrationTurnstileToken(null);
                      registrationTurnstileRef.current?.reset();
                      setClaimError(result.error || 'Request failed.');
                    }
                  } catch (err) {
                    setClaimError('Failed to request ownership.');
                  } finally {
                    setClaimSubmitting(false);
                  }
                }} className={styles.formStack}>
                  <div className={`${styles.inputGroup} ${styles.inputGroupTight}`}>
                    <label>Email</label>
                    <input type="email" className={styles.input} value={formData.email} readOnly />
                  </div>
                  <div className={`${styles.inputGroup} ${styles.inputGroupTight}`}>
                    <label>Password</label>
                    <input type="password" className={styles.input} value={claimPassword} onChange={e => setClaimPassword(e.target.value)} placeholder="Choose a password" required minLength={6} />
                  </div>
                  <div className={`${styles.inputGroup} ${styles.inputGroupTight}`}>
                    <label>Confirm password</label>
                    <input type="password" className={styles.input} value={claimConfirmPassword} onChange={e => setClaimConfirmPassword(e.target.value)} placeholder="Confirm password" required minLength={6} />
                  </div>
                  <div className={`${styles.inputGroup} ${styles.inputGroupTight}`}>
                    <label>Company name</label>
                    <input type="text" className={styles.input} value={formData.companyName} readOnly />
                  </div>
                  <div className={`${styles.inputGroup} ${styles.inputGroupTight}`}>
                    <label>Your name</label>
                    <input type="text" className={styles.input} value={formData.companyRep} readOnly />
                  </div>
                  {claimError && <p className={styles.textDanger} style={{ marginBottom: 8 }}>{claimError}</p>}
                  {registrationTurnstileSiteKey && (
                    <TranzitTurnstileWidget
                      ref={registrationTurnstileRef}
                      siteKey={registrationTurnstileSiteKey}
                      onToken={(token) => setRegistrationTurnstileToken(token)}
                      onExpire={() => setRegistrationTurnstileToken(null)}
                      onError={() => setRegistrationTurnstileToken(null)}
                    />
                  )}
                  <button type="submit" className={styles.loginButton} disabled={claimSubmitting || (!!registrationTurnstileSiteKey && !registrationTurnstileToken)}>{claimSubmitting ? 'Submitting…' : 'Request ownership'}</button>
                  <button type="button" className={`${styles.registerButton} ${styles.buttonSpaced}`} onClick={() => { setShowClaimForm(false); setClaimError(''); setClaimPassword(''); setClaimConfirmPassword(''); }}>Cancel</button>
                </form>
              )}
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <Helmet>
          <title>Registration - {title} - Tranzit.io</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
          <div className={styles.loginBox}>
            <h1>{title}</h1>
            <div className={`${styles.centerBlock} ${styles.textMuted}`}>
              <p>{authorityFailReason}</p>
              {isMcDotAlreadyRegistered && (
                <p style={{ marginTop: '12px', fontSize: '14px' }}>
                  Sign in if you have an account, request an invite from your company admin, or request ownership if you are the authorized company representative.
                </p>
              )}
              {!isEmailExists && !isMcDotAlreadyRegistered && (
                <p>Please ensure your MC and DOT numbers are correct and in good standing with FMCSA, or contact Tranzit support.</p>
              )}
            </div>
            <button
              className={styles.loginButton}
              onClick={() => { setAuthorityFailReason(null); setAuthorityMcDotAlreadyRegistered(false); setError(''); }}
            >
              {isMcDotAlreadyRegistered ? 'Try different MC/DOT' : 'Re-enter information'}
            </button>
            {isMcDotAlreadyRegistered && (
              <button
                type="button"
                className={`${styles.registerButton} ${styles.buttonSpaced}`}
                onClick={() => setShowClaimForm(true)}
              >
                Request ownership
              </button>
            )}
            <button
              type="button"
              className={`${styles.registerButton} ${styles.buttonSpaced}`}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  if (registrationSuccess) {
    return (
      <>
        <Helmet>
          <title>Registration Successful - Tranzit.io</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
        <div className={styles.loginBox}>
          <h1>Registration Successful!</h1>
          <div className={styles.centerBlock}>
            <p>Thank you for registering with Tranzit.io!</p>
            <p>Your account has been created and is pending approval.</p>
            <p>You will receive an email notification once your account is approved.</p>
            <p>This process typically takes 24-48 hours.</p>
          </div>
          <button 
            className={styles.loginButton}
            onClick={() => navigate('/login')}
          >
            Return to Login
          </button>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Sign Up for Tranzit.io - Join the Freight Logistics Platform</title>
        <meta name="title" content="Sign Up for Tranzit.io - Join the Freight Logistics Platform" />
        <meta name="description" content="Create your free Tranzit.io account. Join brokers, carriers, and shippers in the first full-fledged freight logistics marketplace. Streamline operations and unlock new opportunities." />
        <meta name="keywords" content="Tranzit.io sign up, freight logistics registration, carrier registration, broker registration, shipper registration, freight marketplace signup" />
        <link rel="canonical" href="https://tranzit.io/register" />
        
        {/* Performance Optimization - Resource Hints */}
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tranzit.io/register" />
        <meta property="og:title" content="Sign Up for Tranzit.io" />
        <meta property="og:description" content="Create your free Tranzit.io account. Join brokers, carriers, and shippers in the first full-fledged freight logistics marketplace." />
        <meta property="og:image" content="https://tranzit.io/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content="https://tranzit.io/register" />
        <meta name="twitter:title" content="Sign Up for Tranzit.io" />
        <meta name="twitter:description" content="Create your free Tranzit.io account. Join brokers, carriers, and shippers in the first full-fledged freight logistics marketplace." />
        <meta name="twitter:image" content="https://tranzit.io/logo.png" />
      </Helmet>
      
    <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
      <header className={styles.header}>
        <Link to="/" className={styles.logo}>
          <img src={logoImg} alt="Tranzit.io Logo" className={styles.logoImg} loading="eager" width="200" height="50" />
          <div className={styles.textLogo}>
            <span>T</span>
            <span>R</span>
            <span>A</span>
            <span>N</span>
            <span>Z</span>
            <span>I</span>
            <span>T</span>
          </div>
        </Link>
        <nav className={styles.nav}>
          <Link className={styles.menuLink} to="/">Home</Link>
          <Link className={styles.menuLink} to="/technology">Technology</Link>
          <Link className={styles.menuLink} to="/about">About Us</Link>
          <Link className={styles.menuLink} to="/login">Login</Link>
        </nav>
      </header>
      <div className={styles.loginContent}>
        <div className={styles.loginBox}>
        <h1>Create Account</h1>
        
        {inviteLoading && (
          <p className={styles.infoBanner}>Loading invitation…</p>
        )}
        {error && <div className={styles.error}>{error}</div>}
        
        <form onSubmit={handleSubmit} className={styles.form} style={inviteLoading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
          <div className={styles.inputGroup}>
            <label htmlFor="userType">Account Type</label>
            <select
              id="userType"
              value={formData.userType}
              onChange={(e) => setFormData({ ...formData, userType: e.target.value as UserType })}
              className={styles.select}
              disabled={!!new URLSearchParams(location.search).get('invite')}
            >
              <option value="shipper">Shipper</option>
              <option value="carrier">Carrier</option>
              <option value="broker">Broker</option>
            </select>
          </div>

          {/* Broker Sub-Type Selection - Only show when Broker is selected */}
          {formData.userType === 'broker' && (
            <div className={styles.inputGroup}>
              <label>Broker/Carrier Option</label>
              <div className={styles.optionPanel}>
                <input
                  type="checkbox"
                  id="brokerCarrierToggle"
                  checked={formData.brokerSubType === 'broker_carrier'}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    brokerSubType: e.target.checked ? 'broker_carrier' : 'broker_only' 
                  })}
                />
                <label htmlFor="brokerCarrierToggle">
                  We also operate as a carrier
                </label>
                <span className={styles.optionPanelMeta}>
                  {formData.brokerSubType === 'broker_carrier' ? 'Broker + Carrier' : 'Broker Only'}
                </span>
              </div>
            </div>
          )}

          <div className={styles.inputGroup}>
            <label htmlFor="companyName">Company Name<span className={styles.requiredMark}>*</span></label>
            <input
              type="text"
              id="companyName"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              className={styles.input}
              placeholder="Enter your company name"
              required
              disabled={!!new URLSearchParams(location.search).get('invite')}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="companyRep">Company Representative</label>
            <input
              type="text"
              id="companyRep"
              value={formData.companyRep}
              onChange={(e) => setFormData({ ...formData, companyRep: e.target.value })}
              className={styles.input}
              placeholder="Enter company representative (optional)"
              disabled={!!new URLSearchParams(location.search).get('invite')}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="phoneNumber">Phone Number<span className={styles.requiredMark}>*</span></label>
            <input
              type="tel"
              id="phoneNumber"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              className={styles.input}
              placeholder="Enter your phone number"
              required
            />
          </div>

          {(formData.userType === 'carrier' || formData.userType === 'broker') && !new URLSearchParams(location.search).get('invite') && (
            <>
              <div className={styles.inputGroup}>
                <label htmlFor="mcNumber">MC Number <span className={styles.requiredMark}>*</span></label>
                <input
                  type="text"
                  id="mcNumber"
                  value={formData.mcNumber}
                  onChange={(e) => setFormData({ ...formData, mcNumber: e.target.value })}
                  className={styles.input}
                  placeholder="e.g. MC123456 or 123456"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="dotNumber">DOT Number <span className={styles.requiredMark}>*</span></label>
                <input
                  type="text"
                  id="dotNumber"
                  value={formData.dotNumber}
                  onChange={(e) => setFormData({ ...formData, dotNumber: e.target.value })}
                  className={styles.input}
                  placeholder="e.g. 3688871"
                />
              </div>
              <p className={styles.hintText}>
                At least one of MC or DOT is required. We verify with FMCSA/SAFER.
              </p>
            </>
          )}

          {/* Phase 5: Owner vs team member; registration policy (owner only). Hidden when joining via invite (always team member). */}
          {!new URLSearchParams(location.search).get('invite') && (
          <>
          <div className={styles.inputGroup}>
            <label>I am the company owner / team member</label>
            <div className={styles.radioStack}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="isOwner"
                  checked={formData.isOwner === true}
                  onChange={() => setFormData({ ...formData, isOwner: true })}
                />
                <span>
                  <strong>I am the company owner</strong>
                  <span className={styles.radioHint}>
                    Select if YOU own the MC or DOT registering.
                  </span>
                </span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="isOwner"
                  checked={formData.isOwner === false}
                  onChange={() => setFormData({ ...formData, isOwner: false })}
                />
                <span><strong>I am a team member</strong></span>
              </label>
            </div>
          </div>
          {formData.isOwner && (
            <div className={styles.inputGroup}>
              <label>Add team member invite</label>
              <div className={styles.policyRow}>
                <label className={styles.policyLabel}>
                  <input
                    type="radio"
                    name="registrationPolicy"
                    checked={formData.registrationPolicy === REGISTRATION_POLICY.INVITE_ONLY}
                    onChange={() => setFormData({ ...formData, registrationPolicy: REGISTRATION_POLICY.INVITE_ONLY })}
                  />
                  <span>Yes — invite only</span>
                </label>
                <label className={styles.policyLabel}>
                  <input
                    type="radio"
                    name="registrationPolicy"
                    checked={formData.registrationPolicy === REGISTRATION_POLICY.OPEN}
                    onChange={() => setFormData({ ...formData, registrationPolicy: REGISTRATION_POLICY.OPEN })}
                  />
                  <span>No — open</span>
                </label>
              </div>
              <p className={styles.policyNote}>
                Anyone under this MC/DOT will need invite from this account.
              </p>
            </div>
          )}
          </>
          )}

          <div className={styles.inputGroup}>
            <label htmlFor="email">Email<span className={styles.requiredMark}>*</span></label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={styles.input}
              placeholder="Enter your email"
              required
              disabled={!!new URLSearchParams(location.search).get('invite')}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password">Password<span className={styles.requiredMark}>*</span></label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={styles.input}
              placeholder="Create a password"
              required
              minLength={6}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="confirmPassword">Confirm Password<span className={styles.requiredMark}>*</span></label>
            <input
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className={styles.input}
              placeholder="Confirm your password"
              required
              minLength={6}
            />
          </div>

          <TermsOfService 
            onAccept={setTermsAccepted}
            required={true}
            appearance="dark"
          />

          {needsRegistrationTurnstile && registrationTurnstileSiteKey && (
            <TranzitTurnstileWidget
              ref={registrationTurnstileRef}
              siteKey={registrationTurnstileSiteKey}
              onToken={(token) => setRegistrationTurnstileToken(token)}
              onExpire={() => setRegistrationTurnstileToken(null)}
              onError={() => setRegistrationTurnstileToken(null)}
            />
          )}

          <button 
            type="submit" 
            className={styles.loginButton}
            disabled={loading || !termsAccepted || (needsRegistrationTurnstile && !!registrationTurnstileSiteKey && !registrationTurnstileToken)}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <button 
            type="button" 
            className={styles.registerButton}
            onClick={() => navigate('/login')}
          >
            Already have an account? Login
          </button>
        </form>
        </div>
      </div>
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <PrivacyPolicy />
          <ConsentManager />
          <Terms />
          <RefundPolicy />
          <AccessibilityStatement />
        </div>
        <p className={styles.footerCopyright}>&copy; {new Date().getFullYear()} TRANZIT. All rights reserved.</p>
      </footer>
    </div>
    </>
  );
};

export default Register; 